import { useMemo } from "react";
import { useGetThreeSixtyWebSearchListLists360 } from "../../api/threeSixtyLists360";
import { useMyAssignedCheckTypes } from "../../api/ActorAPI";
import { usePermissionValidator } from "../../api/UserPermissionValidators";

import {
  GenericObject,
  HeaderField,
  WithFilterSortHeader,
  WithFilterSortHeaderBaseProps,
  WithFilterSortHeaderInjectedProps,
} from "../FilterSortHeader";
import { ThreeSixtyWebSearchRow } from "../../api/ThreeSixty";
import { ThreeSixtyRowRendererBase } from "../ThreeSixtyRowRendererBase";
import { ThreeSixtyListBase } from "../ThreeSixtyListBase";
import { ThreeSixtyAssignedToCell } from "../ThreeSixtyAssignedToCell";
import { WORKBENCH_ENTRY_SOURCE_WEB_SEARCH } from "../workbenchEntrySource";

type ThreeSixtyWebSearchRowProps = {
  row: ThreeSixtyWebSearchRow | GenericObject;
  cancel: string[];
  toggleCancel: (value: string) => number;
  forceReload: () => void;
};

export const ThreeSixtyWebSearchRowRenderer = (
  props: ThreeSixtyWebSearchRowProps
) => {
  const { row, cancel, toggleCancel, forceReload } = props;
  const perms = usePermissionValidator();

  return (
    <ThreeSixtyRowRendererBase
      row={row}
      cancel={cancel}
      toggleCancel={toggleCancel}
      linkPath="/workbench"
      linkState={{ workbenchEntrySource: WORKBENCH_ENTRY_SOURCE_WEB_SEARCH }}
      canShowLink={perms.canViewWIP360Reports || perms.canEdit360Reports}
      afterDueDateColumns={
        <ThreeSixtyAssignedToCell
          workflowRunUuid={row.workflow_run_uuid}
          lockedBy={row.locked_by}
          onRefresh={forceReload}
        />
      }
    />
  );
};

type ThreeSixtyWebSearchBaseProps = {};
const ThreeSixtyWebSearchBase = (
  props: ThreeSixtyWebSearchBaseProps &
    WithFilterSortHeaderBaseProps &
    WithFilterSortHeaderInjectedProps
) => {
  const perms = usePermissionValidator();

  return (
    <ThreeSixtyListBase
      {...props}
      hasPermission={perms.canView360}
      renderRow={(row, cancel, toggleCancel, forceReload) => (
        <ThreeSixtyWebSearchRowRenderer
          key={row.request_uuid}
          row={row}
          cancel={cancel}
          toggleCancel={toggleCancel}
          forceReload={forceReload}
        />
      )}
    />
  );
};

const ThreeSixtyWebSearchWithFilterSortHeader = WithFilterSortHeader(
  ThreeSixtyWebSearchBase
);

type ThreeSixtyWebSearchProps = {};
const ThreeSixtyWebSearch = (props: ThreeSixtyWebSearchProps) => {
  const { data: assignedCheckTypes } = useMyAssignedCheckTypes();
  const headerDefs = useMemo<HeaderField[][]>(
    () => [
      [
        {
          display: "Name",
          value_field: "candidate_name",
          filterable: true,
          sortable: true,
        },
        {
          display: "Report ID",
          value_field: "request_uuid",
          filterable: false,
          sortable: true,
        },
      ],
      [
        {
          display: "Company",
          value_field: "company_name",
          filterable: true,
          sortable: true,
        },
      ],
      [
        {
          display: "Check Type",
          key_field: "check_type_uuid",
          value_field: "check_type_name",
          filterable: true,
          sortable: true,
          preFilter: assignedCheckTypes?.has_task
            ? assignedCheckTypes.check_type_uuids
            : undefined,
        },
        {
          display: "Behavior Kit",
          value_field: "behavior_kit_name",
          filterable: false,
          sortable: true,
        },
      ],
      [
        {
          display: "Date Added",
          value_field: "requested_at",
          filterable: true,
          sortable: true,
        },
      ],
      [
        {
          display: "Due Date",
          value_field: "due_date",
          filterable: true,
          sortable: true,
        },
      ],
      [
        {
          display: "Assigned To",
          value_field: "locked_by",
          filterable: true,
          sortable: true,
        },
      ],
      [
        {
          display: "Client Ref #",
          value_field: "custom_id",
          filterable: true,
          sortable: true,
        },
      ],
    ],
    [assignedCheckTypes]
  );
  return (
    <ThreeSixtyWebSearchWithFilterSortHeader
      page_size={20}
      date_fields={["requested_at", "due_date"]}
      default_sort_field="due_date"
      default_sort_reverse
      loader={useGetThreeSixtyWebSearchListLists360()}
      header_definitions={headerDefs}
    />
  );
};

export default ThreeSixtyWebSearch;
